/**
 * IndexedDB helpers for crash recovery of audio recordings.
 *
 * v2 layout — the "client gold copy" (Never-Lose-a-Meeting): the browser keeps
 * EVERY chunk of the recording locally until the server confirms the meeting
 * is summarized, not just the not-yet-uploaded tail. Chunks are stored as
 * individual append-only rows so persisting a long recording stays cheap
 * (each save writes only the new chunks, never re-writes the whole buffer):
 *
 *   `${meetingId}::${channel}::v2meta`             → IStoredMeta
 *   `${meetingId}::${channel}::v2chunk::<seq pad8>` → ArrayBuffer
 *
 * v1 records (one monolithic record under the bare meetingId /
 * `${meetingId}::remote`) held only the un-uploaded tail — the recovery replay
 * of such a tail from chunk 0 is what corrupted recordings in the 2026-07-02
 * incident. v1 records remain readable (flagged `legacyTail`) so pre-upgrade
 * crash leftovers can still be recovered via server-state reconciliation.
 *
 * Hardened: every write is verified by reading back immediately; errors are
 * surfaced to callers so the UI can warn users.
 *
 * iOS/Safari compatibility: Blobs cannot be stored in IndexedDB on WebKit
 * (throws DataCloneError). We convert Blob → ArrayBuffer before writing
 * and ArrayBuffer → Blob on read. ArrayBuffer is reliably supported everywhere.
 */

import type { TStreamChannel } from "@lexicanon/shared"
import { DEFAULT_RECORDER_MIME } from "../lib/recorder-mime"

const DB_NAME = "meeting-recorder"
const STORE_NAME = "recordings"

/** v1 record key (kept readable): bare meetingId for room, `::remote` suffix for remote. */
function legacyKey(meetingId: string, channel: TStreamChannel): string {
  return channel === "remote" ? `${meetingId}::remote` : meetingId
}

function metaKey(meetingId: string, channel: TStreamChannel): string {
  return `${meetingId}::${channel}::v2meta`
}

function chunkKeyPrefix(meetingId: string, channel: TStreamChannel): string {
  return `${meetingId}::${channel}::v2chunk::`
}

/** Zero-padded so IndexedDB's lexicographic string-key order == numeric order. */
function chunkKey(meetingId: string, channel: TStreamChannel, seq: number): string {
  return `${chunkKeyPrefix(meetingId, channel)}${String(seq).padStart(8, "0")}`
}

export interface SavedRecording {
  meetingId: string
  chunks: Blob[]
  startTime: number
  savedAt: number
  /** "room" (mic) or "remote" (desktop system-loopback). Absent ⇒ room. */
  channel?: TStreamChannel
  /** True when the meeting was started offline and has no server-side record yet. */
  isProvisional?: boolean
  /** Subject captured when starting offline. Used to create the server meeting on reconnect. */
  subject?: string
  /** Template ID captured when starting offline. Preserved so the correct template is applied on the server. */
  templateId?: string
  /** Remote-only: capture-time offset (s) — preserved so a recovered far-end track stays aligned. */
  offsetSec?: number
  /** Next server upload-batch index at save time (v2 records). 0 ⇒ nothing was uploaded yet. */
  uploadBatchIndex?: number
  /**
   * True for pre-v2 records: `chunks` is only the un-uploaded TAIL of the
   * recording (headerless mid-stream data). Such a record must NEVER be
   * replayed from chunk 0 — the upload has to resume at the server's chunk
   * count instead (see useRecoveryFlow reconciliation).
   */
  legacyTail?: boolean
}

// v1 monolithic storage format (read-only support)
interface IStoredRecording {
  meetingId: string
  buffers: ArrayBuffer[]
  mimeType: string
  startTime: number
  savedAt: number
  channel?: TStreamChannel
  isProvisional?: boolean
  subject?: string
  templateId?: string
  offsetSec?: number
}

// v2 meta record — chunk data lives in separate per-seq rows
interface IStoredMeta {
  version: 2
  meetingId: string
  channel: TStreamChannel
  mimeType: string
  startTime: number
  savedAt: number
  /** Number of chunk rows persisted (seqs 0..chunkCount-1, contiguous). */
  chunkCount: number
  /** Next server upload-batch index at save time. */
  uploadBatchIndex: number
  isProvisional?: boolean
  subject?: string
  templateId?: string
  offsetSec?: number
}

function isStoredRecording(value: unknown): value is IStoredRecording {
  return (
    typeof value === "object" &&
    value !== null &&
    "meetingId" in value &&
    "buffers" in value &&
    Array.isArray((value as IStoredRecording).buffers)
  )
}

function isStoredMeta(value: unknown): value is IStoredMeta {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as IStoredMeta).version === 2 &&
    typeof (value as IStoredMeta).chunkCount === "number"
  )
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbGet<T>(db: IDBDatabase, key: string, guard: (v: unknown) => v is T): Promise<T | null> {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readonly")
    const req = tx.objectStore(STORE_NAME).get(key)
    req.onsuccess = () => resolve(guard(req.result) ? req.result : null)
    req.onerror = () => resolve(null)
  })
}

export interface ISaveChunksOpts {
  isProvisional?: boolean
  subject?: string
  templateId?: string
  channel?: TStreamChannel
  offsetSec?: number
  /** Next server upload-batch index — persisted so recovery knows how much of
   *  the recording the server already holds. */
  uploadBatchIndex?: number
}

/**
 * Append chunks to the meeting's persisted recording and verify the write.
 * Throws on failure so the caller can show a warning to the user.
 *
 * `fromSeq` is the ABSOLUTE sequence number of `chunks[0]` — i.e. how many
 * chunks were produced before the caller's in-memory buffer starts (the
 * upload path trims uploaded chunks from RAM; this keeps the persisted copy
 * complete). Chunks whose seq is already persisted are skipped, so calling
 * with an overlapping window is cheap and idempotent. A gap (fromSeq beyond
 * the persisted count) throws — it would corrupt the reconstruction.
 */
export async function saveChunksToIDB(
  meetingId: string,
  chunks: Blob[],
  startTime: number,
  fromSeq: number,
  opts?: ISaveChunksOpts,
): Promise<void> {
  const channel = opts?.channel ?? "room"
  const mimeType = chunks[0]?.type ?? DEFAULT_RECORDER_MIME

  const db = await openDB()
  try {
    const existing = await idbGet(db, metaKey(meetingId, channel), isStoredMeta)
    const persistedCount = existing?.chunkCount ?? 0

    if (fromSeq > persistedCount) {
      throw new Error(`IDB append gap: buffer starts at seq ${fromSeq} but only ${persistedCount} chunks are persisted`)
    }

    // Only the genuinely new chunks — everything below persistedCount is already stored.
    const newChunks = chunks.slice(persistedCount - fromSeq)
    const buffers = await Promise.all(newChunks.map((b) => b.arrayBuffer()))
    const newCount = fromSeq + chunks.length

    const meta: IStoredMeta = {
      version: 2,
      meetingId,
      channel,
      mimeType: existing?.mimeType ?? mimeType,
      startTime: existing?.startTime ?? startTime,
      savedAt: Date.now(),
      chunkCount: Math.max(persistedCount, newCount),
      uploadBatchIndex: opts?.uploadBatchIndex ?? existing?.uploadBatchIndex ?? 0,
      isProvisional: opts?.isProvisional,
      subject: opts?.subject ?? existing?.subject,
      templateId: opts?.templateId ?? existing?.templateId,
      offsetSec: opts?.offsetSec ?? existing?.offsetSec,
    }

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite")
      const store = tx.objectStore(STORE_NAME)
      buffers.forEach((buf, i) => {
        store.put(buf, chunkKey(meetingId, channel, persistedCount + i))
      })
      store.put(meta, metaKey(meetingId, channel))
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error("IDB transaction failed"))
      tx.onabort = () => reject(tx.error ?? new Error("IDB transaction aborted"))
    })

    // Verify: meta reflects the append and the last chunk row exists.
    const verified = await idbGet(db, metaKey(meetingId, channel), isStoredMeta)
    if (!verified || verified.chunkCount < newCount) {
      throw new Error(
        `IDB write verification failed: expected ${newCount} chunks, meta reports ${verified?.chunkCount ?? 0}`,
      )
    }
    if (verified.chunkCount > 0) {
      const lastChunk = await new Promise<unknown>((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly")
        const req = tx.objectStore(STORE_NAME).get(chunkKey(meetingId, channel, verified.chunkCount - 1))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => resolve(undefined)
      })
      if (!(lastChunk instanceof ArrayBuffer)) {
        throw new Error("IDB write verification failed: last chunk row missing")
      }
    }
  } finally {
    db.close()
  }
}

/** Read all v2 chunk rows for a channel, in seq order. */
function readChunkBuffers(db: IDBDatabase, meetingId: string, channel: TStreamChannel): Promise<ArrayBuffer[]> {
  return new Promise((resolve) => {
    const prefix = chunkKeyPrefix(meetingId, channel)
    const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`)
    const tx = db.transaction(STORE_NAME, "readonly")
    const req = tx.objectStore(STORE_NAME).getAll(range)
    req.onsuccess = () => {
      const rows: unknown[] = Array.isArray(req.result) ? req.result : []
      resolve(rows.filter((r): r is ArrayBuffer => r instanceof ArrayBuffer))
    }
    req.onerror = () => resolve([])
  })
}

export async function loadChunksFromIDB(
  meetingId: string,
  channel: TStreamChannel = "room",
): Promise<SavedRecording | null> {
  try {
    const db = await openDB()
    try {
      // v2 first: meta + chunk rows = the complete recording.
      const meta = await idbGet(db, metaKey(meetingId, channel), isStoredMeta)
      if (meta) {
        const buffers = await readChunkBuffers(db, meetingId, channel)
        if (buffers.length === 0) return null
        return {
          meetingId,
          chunks: buffers.map((buf) => new Blob([buf], { type: meta.mimeType })),
          startTime: meta.startTime,
          savedAt: meta.savedAt,
          channel,
          isProvisional: meta.isProvisional,
          subject: meta.subject,
          templateId: meta.templateId,
          offsetSec: meta.offsetSec,
          uploadBatchIndex: meta.uploadBatchIndex,
        }
      }

      // v1 fallback: a pre-upgrade monolithic record — only the un-uploaded tail.
      const stored = await idbGet(db, legacyKey(meetingId, channel), isStoredRecording)
      if (!stored) return null
      const mimeType = stored.mimeType ?? DEFAULT_RECORDER_MIME
      return {
        meetingId: stored.meetingId,
        chunks: stored.buffers.map((buf) => new Blob([buf], { type: mimeType })),
        startTime: stored.startTime,
        savedAt: stored.savedAt,
        channel: stored.channel ?? "room",
        isProvisional: stored.isProvisional,
        subject: stored.subject,
        templateId: stored.templateId,
        offsetSec: stored.offsetSec,
        legacyTail: true,
      }
    } finally {
      db.close()
    }
  } catch {
    return null
  }
}

export interface ISavedRecordingMeta {
  meetingId: string
  channel: TStreamChannel
  isProvisional?: boolean
  subject?: string
  savedAt: number
  chunkCount: number
}

/**
 * Enumerate every persisted recording in this browser. Recovery used to only
 * probe the meeting you were already looking at, so a crashed *provisional*
 * (offline-started, never given a server id the list knows about) was
 * invisible until you happened to reopen that exact URL.
 */
export async function listSavedRecordings(): Promise<ISavedRecordingMeta[]> {
  try {
    const db = await openDB()
    try {
      const keys = await new Promise<IDBValidKey[]>((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly")
        const req = tx.objectStore(STORE_NAME).getAllKeys()
        req.onsuccess = () => resolve(Array.isArray(req.result) ? req.result : [])
        req.onerror = () => resolve([])
      })

      const found: ISavedRecordingMeta[] = []
      const seen = new Set<string>()

      for (const raw of keys) {
        if (typeof raw !== "string") continue
        if (raw.includes("::v2chunk::")) continue

        if (raw.endsWith("::v2meta")) {
          const meta = await idbGet(db, raw, isStoredMeta)
          if (!meta || meta.chunkCount <= 0) continue
          const id = `${meta.meetingId}::${meta.channel}`
          if (seen.has(id)) continue
          seen.add(id)
          found.push({
            meetingId: meta.meetingId,
            channel: meta.channel,
            isProvisional: meta.isProvisional,
            subject: meta.subject,
            savedAt: meta.savedAt,
            chunkCount: meta.chunkCount,
          })
          continue
        }

        // v1 monolithic record (bare meetingId or `${id}::remote`)
        if (raw.includes("::v2")) continue
        const stored = await idbGet(db, raw, isStoredRecording)
        if (!stored || stored.buffers.length === 0) continue
        const channel: TStreamChannel = stored.channel ?? (raw.endsWith("::remote") ? "remote" : "room")
        const id = `${stored.meetingId}::${channel}`
        if (seen.has(id)) continue
        seen.add(id)
        found.push({
          meetingId: stored.meetingId,
          channel,
          isProvisional: stored.isProvisional,
          subject: stored.subject,
          savedAt: stored.savedAt,
          chunkCount: stored.buffers.length,
        })
      }

      return found.sort((a, b) => b.savedAt - a.savedAt)
    } finally {
      db.close()
    }
  } catch {
    return []
  }
}

/**
 * Delete a meeting's persisted chunks (v1 record + v2 meta/chunk rows). With
 * no `channel`, clears BOTH the room and remote records; with a `channel`,
 * clears just that one. Called once the server confirms the meeting is
 * SUMMARIZED — never merely on upload success, so the local gold copy
 * outlives any server-side processing mishap.
 */
export async function clearChunksFromIDB(meetingId: string, channel?: TStreamChannel): Promise<void> {
  try {
    const db = await openDB()
    const channels: TStreamChannel[] = channel ? [channel] : ["room", "remote"]
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite")
      const store = tx.objectStore(STORE_NAME)
      for (const ch of channels) {
        store.delete(legacyKey(meetingId, ch))
        store.delete(metaKey(meetingId, ch))
        const prefix = chunkKeyPrefix(meetingId, ch)
        store.delete(IDBKeyRange.bound(prefix, `${prefix}\uffff`))
      }
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
      tx.onabort = () => resolve()
    })
    db.close()
  } catch {
    // Best-effort — if this fails, recovery data stays (which is the safe outcome)
  }
}

/**
 * Check if IndexedDB storage quota is sufficient.
 * Returns { ok: true } if >= 500MB available, or if the API is unavailable.
 */
export async function checkStorageQuota(): Promise<{ available: number; ok: boolean }> {
  if (!navigator.storage?.estimate) return { available: Number.POSITIVE_INFINITY, ok: true }
  try {
    const { quota = 0, usage = 0 } = await navigator.storage.estimate()
    const available = quota - usage
    return { available, ok: available > 500 * 1024 * 1024 }
  } catch {
    return { available: Number.POSITIVE_INFINITY, ok: true }
  }
}
